import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";

/**
 * TesterCountdownBanner — sticky banner under the header that announces
 * the "First 200 Sciencers" beta test. Polls /api/tester-program every 30 s
 * so the live count drops in real-time as people join.
 */
export default function TesterCountdownBanner({ onJoin, isOnboarded, role }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/tester-program`, { cache: "no-store" });
        if (r.ok && !cancelled) setStatus(await r.json());
      } catch {/* network noise — try next tick */}
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!status || dismissed) return null;

  // Already a tester? Show celebration pill instead.
  if (isOnboarded && role === "tester") {
    return (
      <div
        className="w-full text-center text-xs sm:text-sm font-bold py-2 px-3 no-print"
        style={{ background: "var(--orange)", color: "#fff", borderBottom: "2.5px solid var(--ink)" }}
        data-testid="tester-banner"
      >
        🧪 You're a Founding Tester! Help Sean break things & he'll thank you at launch 🧡
      </div>
    );
  }

  if (isOnboarded && role === "observer") {
    return (
      <div
        className="w-full text-center text-xs sm:text-sm font-bold py-2 px-3 no-print"
        style={{ background: "var(--navy)", color: "#fff", borderBottom: "2.5px solid var(--ink)" }}
        data-testid="tester-banner"
      >
        🔭 You're an Observer — the testers locked their spots first. You'll get full access at public launch.
      </div>
    );
  }

  // Public banner
  const { spots_left, joined, limit } = status;
  if (spots_left <= 0) {
    return (
      <div
        className="w-full text-center text-xs sm:text-sm font-bold py-2 px-3 no-print"
        style={{ background: "var(--ink)", color: "#fff" }}
        data-testid="tester-banner"
      >
        🔒 All 200 tester spots are LOCKED. Join now to become a launch-day Observer →{" "}
        <button onClick={onJoin} className="underline" data-testid="tester-banner-cta">Join Lab</button>
      </div>
    );
  }

  return (
    <div
      className="relative w-full no-print"
      style={{ background: "var(--orange)", color: "#fff", borderBottom: "2.5px solid var(--ink)" }}
      data-testid="tester-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs sm:text-sm">
        <span className="font-bold tracking-wide">🧪 FOUNDING TESTERS</span>
        <span>
          Only <strong className="font-mono" data-testid="tester-spots-left">{spots_left}</strong> of {limit} spots left.
        </span>
        <span className="hidden sm:inline opacity-90">First 200 Sciencers test the lab for a month before public launch.</span>
        <button
          onClick={onJoin}
          data-testid="tester-banner-cta"
          className="font-bold underline underline-offset-2 hover:no-underline"
        >
          Claim mine →
        </button>
      </div>
      <button
        onClick={() => setDismissed(true)}
        aria-label="dismiss"
        className="absolute right-2 top-1.5 sm:top-2 w-6 h-6 rounded-full border-2 border-white bg-transparent text-white text-xs flex items-center justify-center"
        data-testid="tester-banner-dismiss"
      >
        ×
      </button>
    </div>
  );
}
